import React from 'react';
import { Clock, DollarSign, RefreshCw, Settings, HeadphonesIcon, X, Check, Monitor, Wand2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function Comparison() {
  const {
    t
  } = useLanguage();
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useScrollAnimation();
  const {
    ref: tableRef,
    isVisible: tableVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const getIcon = (key: string) => {
    switch (key) {
      case 'time':
        return <Clock className="w-4 h-4 text-gray-400" data-id="element-46" />;
      case 'cost':
        return <DollarSign className="w-4 h-4 text-gray-400" data-id="element-47" />;
      case 'revisions':
        return <RefreshCw className="w-4 h-4 text-gray-400" data-id="element-48" />;
      case 'maintenance':
        return <HeadphonesIcon className="w-4 h-4 text-gray-400" data-id="element-49" />;
      case 'control':
        return <Settings className="w-4 h-4 text-gray-400" data-id="element-50" />;
      default:
        return null;
    }
  };
  const rows = [{
    key: 'time',
    ...t.comparison.rows.time
  }, {
    key: 'cost',
    ...t.comparison.rows.cost
  }, {
    key: 'revisions',
    ...t.comparison.rows.revisions
  }, {
    key: 'maintenance',
    ...t.comparison.rows.maintenance
  }, {
    key: 'control',
    ...t.comparison.rows.control
  }];
  const intro = (t.comparison as any).intro;
  return <section id="comparison" className="py-24 bg-[#050505]" data-id="element-51">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-52">
        <div ref={titleRef} className={`text-center mb-8 animate-on-scroll ${titleVisible ? 'animate-visible' : ''}`} data-id="element-53">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-54">
            {t.comparison.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-55">
            {t.comparison.subtitle}
          </p>
        </div>

        {intro && <p className="text-sm text-gray-400 leading-relaxed max-w-3xl mx-auto text-center mb-12" data-id="element-56">
            {intro}
          </p>}

        {/* Desktop: Real HTML Table with semantic SEO structure */}
        <div ref={tableRef} className={`hidden md:block overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0A] animate-on-scroll ${tableVisible ? 'animate-visible' : ''}`} data-id="element-57">
          <table className="w-full" role="table" aria-label={t.comparison.title} data-id="element-58">
            <caption className="sr-only" data-id="element-59">{t.comparison.subtitle}</caption>
            <thead data-id="element-60">
              <tr className="bg-white/5 border-b border-white/10" data-id="element-61">
                <th scope="col" className="p-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-center w-1/3" data-id="element-62">
                  {t.comparison.feature}
                </th>
                <th scope="col" className="p-5 text-xs font-bold text-gray-300 uppercase tracking-wider text-center w-1/3" data-id="element-63">
                  <span className="inline-flex items-center justify-center gap-2" data-id="element-64">
                    <Monitor className="w-4 h-4 text-gray-500" data-id="element-65" />
                    {t.comparison.studio}
                  </span>
                </th>
                <th scope="col" className="p-5 text-xs font-bold text-gray-300 uppercase tracking-wider text-center w-1/3" data-id="element-66">
                  <span className="inline-flex items-center justify-center gap-2" data-id="element-67">
                    <Wand2 className="w-4 h-4 text-accent-blue" data-id="element-68" />
                    {t.comparison.ai}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-69">
              {rows.map((row, idx) => <tr key={idx} data-id="element-70">
                  <td className="p-5 text-white font-medium text-sm text-center" data-id="element-71">
                    <span className="inline-flex items-center justify-center gap-3" data-id="element-72">
                      {getIcon(row.key)}
                      {row.name}
                    </span>
                  </td>
                  <td className="p-5 text-gray-400 text-center text-sm border-r border-white/5" data-id="element-73">
                    <span className="inline-flex items-center justify-center gap-3" data-id="element-74">
                      <span className="w-6 h-6 rounded-full bg-white/5 inline-flex items-center justify-center flex-shrink-0" data-id="element-75">
                        <X className="w-3 h-3 text-gray-500" data-id="element-76" />
                      </span>
                      {row.studio}
                    </span>
                  </td>
                  <td className="p-5 text-white font-medium text-center text-sm" data-id="element-77">
                    <span className="inline-flex items-center justify-center gap-3" data-id="element-78">
                      <span className="w-6 h-6 rounded-full bg-accent-blue/10 inline-flex items-center justify-center flex-shrink-0" data-id="element-79">
                        <Check className="w-3 h-3 text-accent-blue" data-id="element-80" />
                      </span>
                      {row.ai}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3" data-id="element-81">
          {rows.map((row, idx) => <ComparisonCard key={idx} row={row} idx={idx} getIcon={getIcon} t={t} data-id="element-82" />)}
        </div>
      </div>

      {/* Noscript fallback — visible to crawlers without JS */}
      <noscript data-id="element-83">
        <div style={{
        padding: '2rem',
        background: '#050505',
        color: '#fff'
      }} data-id="element-84">
          <h2 data-id="element-85">{t.comparison.title}</h2>
          <p data-id="element-86">{t.comparison.subtitle}</p>
          <table data-id="element-87">
            <thead data-id="element-88">
              <tr data-id="element-89">
                <th data-id="element-90">{t.comparison.feature}</th>
                <th data-id="element-91">{t.comparison.studio}</th>
                <th data-id="element-92">{t.comparison.ai}</th>
              </tr>
            </thead>
            <tbody data-id="element-93">
              {rows.map((row, idx) => <tr key={idx} data-id="element-94">
                  <td data-id="element-95">{row.name}</td>
                  <td data-id="element-96">{row.studio}</td>
                  <td data-id="element-97">{row.ai}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </noscript>
    </section>;
}
function ComparisonCard({
  row,
  idx,
  getIcon,
  t
}: {
  row: any;
  idx: number;
  getIcon: any;
  t: any;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const delayClass = `delay-${idx * 100}`;
  return <div ref={ref} className={`bg-[#0A0A0A] rounded-xl border border-white/10 p-4 animate-hidden animate-fade-up ${isVisible ? 'animate-visible' : ''} ${delayClass}`} data-id="element-98">
      <h3 className="text-sm font-bold text-white mb-3 pb-2 border-b border-white/10 flex items-center justify-center gap-2" data-id="element-99">
        {getIcon(row.key)}
        {row.name}
      </h3>
      <div className="space-y-3" data-id="element-100">
        <div className="flex justify-between items-center" data-id="element-101">
          <span className="text-xs text-gray-500 font-medium flex items-center gap-1" data-id="element-102">
            <Monitor className="w-3 h-3" data-id="element-103" />
            {t.comparison.studio}
          </span>
          <span className="text-sm text-gray-400 text-right flex items-center gap-2" data-id="element-104">
            <span className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0" data-id="element-105">
              <X className="w-3 h-3 text-gray-500" data-id="element-106" />
            </span>
            {row.studio}
          </span>
        </div>
        <div className="flex justify-between items-center" data-id="element-107">
          <span className="text-xs text-gray-400 font-medium flex items-center gap-1" data-id="element-108">
            <Wand2 className="w-3 h-3 text-accent-blue" data-id="element-109" />
            {t.comparison.ai}
          </span>
          <span className="text-sm text-white font-medium text-right flex items-center gap-2" data-id="element-110">
            <span className="w-5 h-5 rounded-full bg-accent-blue/10 flex items-center justify-center flex-shrink-0" data-id="element-111">
              <Check className="w-3 h-3 text-accent-blue" data-id="element-112" />
            </span>
            {row.ai}
          </span>
        </div>
      </div>
    </div>;
}